import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RouteComponentProps } from 'react-router-dom';
import { connect } from 'redux-scaffolding-ts';
import { buildRequestFormViewModel, RequestFormStore, RequestFormViewModel } from 'stores/requests/request-form-store';
import { RequestDto, RequestsStore } from 'stores/requests/requests-store';
import { ToastComponent } from '../../util/toast-component';
import TailwindMessage from '../alerts/message-tw';
import BottomToolbarActions from '../footer/requestBottomToolbar';
import RequestCommentsContainer from './request-comments-container';
import RequestDetailContainer from './request-detail-container';
import RequestEventContainer from './request-event-container';
import RequestInstructorContainer from './request-instructor-container';
import RequestMainData from './request-main-data';
import RequestStudentContainer from './request-student-container';
import RequestSupportDetailsContainer from './request-support-detail-container';
import RequestTabsComponent from './request-tabs-component';
import RequestTrainingDetailsContainer from './request-training-details-container';

interface RequestContainerProp extends RouteComponentProps {
  selectedItem?: RequestFormViewModel;
  onBack?: () => void;
  requestStore?: RequestsStore;
  fromEventTab?: boolean;
  requestFormStore?: RequestFormStore;
}

const RequestContainer: React.FC<RequestContainerProp> = ({
  selectedItem,
  onBack,
  requestStore,
  fromEventTab,
  requestFormStore,
  history
}) => {
  const [activeTab, setActiveTab] = useState<string>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [request, setRequest] = useState<RequestDto>(null);
  const [requestFormViewModel, setRequestFormViewModel] = useState<RequestFormViewModel>(null);
  const [tabs, setTabs] = useState<{ menuItem: string }[]>([]);
  const [editMode, setEditMode] = useState(false);
  const [errMsgs, setErrMsgs] = useState<string[]>([]);
  const { t } = useTranslation();

  const loadRequest = useCallback(
    async (id: string) => {
      try {
        let elem = await requestStore.getById(id);
        const viewModel = buildRequestFormViewModel(elem);

        const buildPanesLocal = (viewModel: RequestFormViewModel) => {
          if (!viewModel) return [];
          const { hasEvent } = viewModel;
          let tabPanes = [];
          if (viewModel.hasRequestDetails) tabPanes.push({ menuItem: t('Request Details') });
          if (viewModel.hasEventDetails) tabPanes.push({ menuItem: t('Event Details') });
          if (viewModel.hasSupportDetails) tabPanes.push({ menuItem: t('Support Details') });
          if (viewModel.hasStudents && viewModel.eventTypeCategory !== 30) tabPanes.push({ menuItem: t('Students') });
          if (viewModel.hasInstructors && viewModel.eventTypeCategory !== 30) tabPanes.push({ menuItem: t('Instructors') });
          if (hasEvent && !fromEventTab) tabPanes.push({ menuItem: t('Event') });
          tabPanes.push({ menuItem: t('Comments') });
          return tabPanes;
        };

        const panes = buildPanesLocal(viewModel);
        const firstTab = (panes || [])[0]?.menuItem;

        setRequest(elem);
        setRequestFormViewModel(viewModel);
        setActiveTab(firstTab);
        setTabs(panes);
        setEditMode(false);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    },
    [requestStore, fromEventTab, t]
  );

  useEffect(() => {
    if (!selectedItem?.id) return;
    setLoading(true);
    loadRequest(selectedItem.id);
  }, [selectedItem?.id, loadRequest]);

  const handleComments = (comments: string) => {
    if (!requestFormViewModel) return;
    const copy = { ...requestFormViewModel, comments };
    setRequestFormViewModel(copy);
    requestFormStore?.change({ comments });
  };

  const handleEditClick = () => {
    setEditMode(true); // Entrar en edición
  };

  const handleSaveClick = async () => {
    requestFormStore.change({ ...requestFormViewModel });
    setLoading(true);
    await requestFormStore
      .updateRequest()
      .then(result => {
        if (result?.isSuccess) {
          setEditMode(!result.isSuccess);
          setLoading(false);
          ToastComponent({ text: 'Request saved successfully!', type: 'success-toast' });
        } else {
          setLoading(false);
        }
      })
      .catch(err => {
        console.error(err);
        (requestFormStore.state.result?.messages || []).forEach(msg => ToastComponent({ text: msg.body, type: 'warning-toast' }));
        setLoading(false);
      });
  };

  const handleErrors = (errors: string[]) => {
    setErrMsgs(errors);
  };

  const handleCancel = () => {
    onBack && onBack();
    setEditMode(false);
  };

  const handleOnDataChange = (data: Partial<RequestFormViewModel>) => {
    if (!requestFormViewModel) return;
    const copy = { ...requestFormViewModel, ...data };
    setRequestFormViewModel(copy);
    requestFormStore?.change(copy);
  };

  const handleBeginAccept = (value: boolean) => {
    if (value) {
      setLoading(true);
    }
  };

  return (
    <div
      className="min-h-screen p-4 rounded-md shadow-md relative max-w-4xl mx-auto"
      style={{ paddingBottom: '120px' /* espacio para toolbar */ }}
    >
      {/* Header solo con Back */}
      <div className="flex items-center mb-2">
        <button
          onClick={() => {
            onBack && onBack();
            setEditMode(false);
          }}
          className="flex items-center gap-1 text-sm text-black-600"
        >
          ← Back
        </button>
      </div>

      {loading || !request ? (
        <div className="flex items-center justify-center p-4">
          <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
          <span className="ml-2 text-gray-600">{t('Loading request...')}</span>
        </div>
      ) : (
        <>
          {requestFormStore?.state &&
            requestFormStore.state?.result &&
            requestFormStore.state.result?.messages &&
            requestFormStore.state.result?.messages.length > 0 && (
              <TailwindMessage
                error
                icon="exclamation-circle"
                header={t('An error ocurred')}
                list={[...requestFormStore.state.result?.messages.map(o => o.body).filter(x => x !== ''), ...errMsgs]}
                className="mb-4"
              />
            )}
          <RequestMainData selectedItem={requestFormViewModel} readOnly={!editMode} onChange={data => handleOnDataChange(data)} />
          <RequestTabsComponent activeTab={activeTab} tabs={tabs} onRequestTabClick={setActiveTab} />

          <div className="min-h-[150px]">
            {activeTab === 'Request Details' && (
              <RequestDetailContainer
                request={requestFormViewModel}
                readOnly={!editMode}
                requestFormStore={requestFormStore}
                onChange={data => handleOnDataChange(data)}
              />
            )}
            {activeTab === 'Event Details' && (
              <RequestTrainingDetailsContainer
                request={requestFormViewModel}
                readOnly={!editMode}
                onChange={data => handleOnDataChange(data)}
              />
            )}
            {activeTab === 'Support Details' && (
              <RequestSupportDetailsContainer
                request={requestFormViewModel}
                readOnly={!editMode}
                onChange={data => handleOnDataChange(data)}
              />
            )}
            {activeTab === 'Students' && (
              <RequestStudentContainer request={requestFormViewModel} readOnly={!editMode} onChange={data => handleOnDataChange(data)} />
            )}
            {activeTab === 'Instructors' && (
              <RequestInstructorContainer request={requestFormViewModel} readOnly={!editMode} onChange={data => handleOnDataChange(data)} />
            )}
            {activeTab === 'Event' && <RequestEventContainer request={requestFormViewModel} fromRequestTab={true} />}
            {activeTab === 'Comments' && (
              <RequestCommentsContainer readOnly={!editMode} comment={requestFormViewModel.comments} onChange={handleComments} />
            )}
          </div>

          <BottomToolbarActions
            history={history}
            request={buildRequestFormViewModel(request)}
            readOnly={!editMode}
            canEdit={!editMode}
            onClose={handleCancel}
            onEdit={handleEditClick}
            onSave={handleSaveClick}
            onErrors={handleErrors}
            onBeginAccept={value => handleBeginAccept(value)}
            sidebarWidthPx={56}
          />
        </>
      )}
    </div>
  );
};

export default connect(['requestStore', RequestsStore], ['requestFormStore', RequestFormStore])(RequestContainer);
